import { useCallback } from 'react';

import { CONFIRM_POPUP_MODE, useConfirmPopup } from 'src/components/ConfirmPopup';
import { captureMessage } from 'src/lib/sentry';
import { useErrorHandler } from 'src/services/useErrorHandler';
import { useImageStyles } from 'src/services/useImageStyles';
import { useWithVisualLanguageInfo } from 'src/services/useWithVisualLanguageInfo';
import { useImageStyleActions } from 'src/store/imageStyle';
import { useNotificationActions } from 'src/store/notification';
import { useVisualLanguageActions } from 'src/store/visualLanguage';
import { useImageStylePopup } from 'src/views/ImageStylePopup';

export const usePrepareImageStyleListProps = () => {
  const { generalImageStyles, workspaceImageStyles } = useImageStyles();
  const enrichedWorkspaceImageStyles = useWithVisualLanguageInfo(workspaceImageStyles);

  const handleError = useErrorHandler();
  const openImageStylePopup = useImageStylePopup();
  const confirmDelete = useConfirmDelete();

  const notifications = useNotificationActions();
  const visualLanguageActions = useVisualLanguageActions();
  const imageStyleActions = useImageStyleActions();

  const onCreateStyle = useCallback(async () => {
    // use the default brand voice to create a new image style
    const visualLanguage = visualLanguageActions.getDefaultVisualLanguage();
    if (!visualLanguage) {
      notifications.displayWarning('Default visual language not found');
      return;
    }

    const result = await openImageStylePopup({ visualLanguage });
    if (result) {
      notifications.displaySuccess(`Image style '${result.name}' created`);
    }
  }, [visualLanguageActions, notifications, openImageStylePopup]);

  const onEditStyle = useCallback(
    async (imageStyle) => {
      const result = await openImageStylePopup({
        visualLanguage: imageStyle.visualLanguage,
        imageStyle,
      });
      if (result) {
        notifications.displaySuccess(`Image style '${result.name}' updated`);
      }
    },
    [notifications, openImageStylePopup]
  );

  const onDeleteStyle = useCallback(
    async (imageStyle) => {
      try {
        const confirmed = await confirmDelete();
        if (!confirmed) {
          return;
        }

        await imageStyleActions.deleteImageStyle(imageStyle.id);
        notifications.displaySuccess(`Image style '${imageStyle?.name}' deleted`);
      } catch (error) {
        handleError(error);
      }
    },
    [confirmDelete, handleError, imageStyleActions, notifications]
  );

  const onSelect = useCallback(() => {
    captureMessage('No real onSelect handler provided for ImageStyleList', {
      level: 'error',
      extra: {
        source: 'usePrepareImageStyleListProps',
      },
    });
  }, []);

  return {
    generalStyles: generalImageStyles,
    customStyles: enrichedWorkspaceImageStyles,
    onSelect,
    onCreateStyle,
    onEditStyle,
    onDeleteStyle,
  };
};

const useConfirmDelete = () => {
  const confirmPopup = useConfirmPopup();

  return useCallback(() => {
    return confirmPopup({
      mode: CONFIRM_POPUP_MODE.warning,
      title: 'Delete style',
      content: 'Are you sure you want to delete this style?',
      buttons: {
        confirm: 'Delete',
      },
      withReject: true,
    })
      .then(() => true)
      .catch(() => false);
  }, [confirmPopup]);
};
